// General
import React, { useState, useContext, useRef, useMemo } from "react";
// Core / Table
import TablePage from "../core/TablePage";
// Modals
import OfferAddModal from "../modals/offer/OfferAddModal";
import OfferUpdateModal from "../modals/offer/OfferUpdateModal";
// Contexts
import { OfferContext } from "../data/offer/OfferContext";
import { ServiceContext } from "../data/services/ServiceContext";
import { CustomerContext } from "../data/customer/CustomerContext";
// UI / PrimeReact
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
// Icons
import { FaTrash, FaPencilAlt } from "react-icons/fa";
// General Data / Utility
import { useData } from "../data/general/DataContext";

const OfferPage = () => {
  const { offers, deleteOffer, updateOffer } = useContext(OfferContext);
  const { services } = useContext(ServiceContext);
  const { customers } = useContext(CustomerContext);
  const { windowMode } = useData();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);

  // Tekliflere müşteri adı + servisler + toplam ekle
  const offersWithServices = useMemo(() => {
    return offers.map((offer) => {
      // müşteri adı bul
      const customer = customers.find((c) => c.id === offer.customerId);

      // servisler
      const offerItems =
        offer.items && offer.items.length
          ? offer.items.map((s) => ({
            ...s,
            serviceFullName: s.serviceFullName || s.name,
          }))
          : services
            .filter((s) => s.customerId === offer.customerId)
            .map((s) => ({
              id: s.id,
              serviceFullName: `${s.brand} ${s.device} ${s.deviceModel || ""}`.trim(),
              quantity: s.quantity || 1,
              price: s.price || 0,
            }));

      // toplam hesapla
      const totalAmount = offerItems.reduce(
        (sum, s) => sum + (s.quantity || 1) * (s.price || 0) * (1 + (offer.vatRate || 0) / 100),
        0
      );

      return {
        ...offer,
        items: offerItems,
        customerName: customer ? customer.fullName : "Bilinmiyor",
        serviceNames: offerItems.map((s) => s.serviceFullName).join(", "),
        totalAmount,
      };
    });
  }, [offers, services, customers]);

  const ActionMenu = ({ rowData }) => {
    const op = useRef(null);
    if (windowMode === "mobile") return null;
    return (
      <>
        <Button
          label="..."
          className="p-button-rounded p-button-text"
          style={{ minWidth: "40px", fontSize: "22px", fontWeight: "bold" }}
          onClick={(e) => op.current.toggle(e)}
        />
        <OverlayPanel
          ref={op}
          dismissable
          showCloseIcon={false}
          style={{ padding: "0.5rem" }}
        >
          <div className="flex flex-col gap-1">
            <Button
              icon={<FaPencilAlt />}
              label="Düzenle"
              className="p-button-text w-full justify-start"
              style={{ fontSize: "14px", gap: "10px" }}
              onClick={() => {
                setSelectedOffer(rowData);
                setShowUpdateModal(true);
                op.current.hide();
              }}
            />
            <div className="w-full border-t border-gray-300 my-1"></div>
            <Button
              icon={<FaTrash />}
              label="Sil"
              className="p-button-text w-full justify-start"
              style={{ color: "red", fontSize: "14px", gap: "10px" }}
              onClick={() => {
                deleteOffer(rowData.id);
                op.current.hide();
              }}
            />
          </div>
        </OverlayPanel>
      </>
    );
  };

  const MobileActions = ({ rowData }) =>
    windowMode === "mobile" ? (
      <div className="flex gap-2 mt-2">
        <Button
          icon={<FaPencilAlt />}
          label="Düzenle"
          className="p-button-sm p-button-rounded p-button-success flex-1"
          onClick={() => {
            setSelectedOffer(rowData);
            setShowUpdateModal(true);
          }}
        />
        <Button
          icon={<FaTrash />}
          label="Sil"
          className="p-button-sm p-button-rounded p-button-danger flex-1"
          onClick={() => deleteOffer(rowData.id)}
        />
      </div>
    ) : null;

  const columns = [
    { field: "id", header: "ID", hideInFilter: true },
    { field: "customerName", header: "Müşteri" },
    {
      field: "serviceNames",
      header: "Servisler",
      body: (rowData) => {
        const text = rowData.serviceNames || "";
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "250px",
            }}
            title={text}
          >
            {text}
          </div>
        );
      },
    },
    { field: "offerDate", header: "Teklif Tarihi" },
    { field: "plannedDate", header: "Teslim Tarihi" },
    {
      field: "vatRate",
      header: "KDV (%)",
      body: (rowData) => `${rowData.vatRate}%`,
    },
    {
      field: "totalAmount",
      header: "Genel Toplam",
      body: (rowData) => `${rowData.totalAmount.toFixed(2)} ₺`,
    },
    { field: "status", header: "Durum" },
    {
      header: "İşlemler",
      body: (rowData) =>
        windowMode === "mobile" ? (
          <div className="flex gap-2 mt-2 w-full">
            <Button
              icon={<FaPencilAlt />}
              label="Düzenle"
              className="flex-1"
              style={{
                backgroundColor: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "6px",
                height: "26px",     
                fontSize: "10px",    
                justifyContent: "center",
                gap: "4px",
                padding: "0 6px",   
              }}
              onClick={() => {
                setSelectedOffer(rowData);
                setShowUpdateModal(true);
              }}
            />
            <Button
              icon={<FaTrash />}
              label="Sil"
              className="flex-1"
              style={{
                backgroundColor: "#ef4444",
                color: "white",
                border: "none",
                borderRadius: "6px",
                height: "26px",      
                fontSize: "10px",    
                justifyContent: "center",
                gap: "4px",
                padding: "0 6px",    
              }}
              onClick={() => deleteOffer(rowData.id)}
            />
          </div>
        ) : (
          <ActionMenu rowData={rowData} />
        ),
      hideInFilter: true,
    },
  ];
//Filtre Kolonları 
  const columnFilterOptions = {
    customerName: { type: "text" },
    serviceNames: { type: "text" },
    offerDate: { type: "date" },
    plannedDate: { type: "date" },
    status: {
      type: "checkbox",
      options: ["Beklemede", "Onaylandı", "Reddedildi"],
    },
  };

  return (
    <>
      <div className="p-6">
        <TablePage
          title="Teklifler"
          subTitle="Teklif ekleyebilir, güncelleyebilir veya silebilirsiniz."
          columns={columns}
          data={offersWithServices}
          defaultRows={10}
          rowsPerPageOptions={[10, 15, 25, 50,100]}
          showSearch
          showDateFilter
          filterable
          onAdd={() => setShowAddModal(true)}
          columnFilterOptions={columnFilterOptions}
          rowExpansionTemplate={(rowData) => <MobileActions rowData={rowData} />}
        />
      </div>

      {showAddModal && (
        <OfferAddModal
          show={showAddModal}
          handleClose={() => setShowAddModal(false)}
          onSubmit={() => setShowAddModal(false)}
        />
      )}
      {showUpdateModal && selectedOffer && (
        <OfferUpdateModal
          show={showUpdateModal}
          handleClose={() => setShowUpdateModal(false)}
          offer={selectedOffer}
          onSubmit={(data) => {
            updateOffer(selectedOffer.id, data);
            setShowUpdateModal(false);
          }}
        />
      )}
    </>
  );
};

export default OfferPage;
